import {
    Directive,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    Output,
    SimpleChanges
} from '@angular/core';
import {ContenteditableHelper} from './ContenteditableHelper';
import {InputSelection} from './InputSelection';

/**
 * This directive solves the following problems for all browsers
 * - Consistent two-way binding
 * - Max length limitation (typing and copy/paste)
 * - Cleaning of HTML polluted text while inserting
 *
 * A contenteditable is not yet supported by angular. With the [textContent]='value' and (input)='...'
 * does not work in IE and Firefox, as after every keystroke the cursor is set to position 0
 *
 * This solution we have from Stackoverflow:
 * https://stackoverflow.com/questions/35378087/how-to-use-ngmodel-on-divs-contenteditable-in-angular2/41253897#41253897
 */
@Directive({
    selector: '[contenteditableModel]',
    standalone: false
})
export class ContenteditableModelDirective implements OnChanges {

    @Input()
    public contenteditableModel!: string;

    @Input()
    public maxLength: number | undefined;


    @Output()
    public contenteditableModelChange = new EventEmitter();

    private lastViewModel: string | undefined;

    constructor(private elementRef: ElementRef) {
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['contenteditableModel'] !== undefined && changes['contenteditableModel'].currentValue !== this.lastViewModel) {
            this.lastViewModel = this.contenteditableModel;
            this.refreshView();
        }


        if (changes['contenteditableModel'] !== undefined && changes['contenteditableModel'].currentValue.trim().length === 0) {
            // Firefox does create a "<br>"-Tag when removing all characters in a contenteditable
            // To get the placeholder working, we manually clear the innerHTML of the contentEditable
            // ElementRef data is any

            this.elementRef.nativeElement.innerHTML = '';
        }
    }

    @HostListener('keydown', ['$event'])
    public onKeydown(event: KeyboardEvent): void {
        const maxLength = this.maxLength;
        if (this.hasMaxInputLength(maxLength) && !ContenteditableHelper.isAllowedKeyCode(event) && !this.hasSelection() && this.getText().length >= maxLength) {
            event.preventDefault();
        }
    }

    @HostListener('keyup')
    public onKeyup(): void {
        this.bindInput();
    }

    @HostListener('input')
    public onInput(): void {
        // Only for iOS autocomplete: To set the model and shorten the text if necessary
        this.bindInput();
    }

    @HostListener('paste', ['$event'])
    public onPaste(event: Event): void {
        event.preventDefault();

        let insertText = ContenteditableHelper.getClipboardContent(event);

        const maxLength = this.maxLength;
        if (this.hasMaxInputLength(maxLength)) {
            const selection = this.getSelection();
            const selectionLength = selection !== undefined ? selection.end - selection.start : 0;
            const maxInsertLength = maxLength - this.getText().length + selectionLength;
            insertText = insertText.substr(0, Math.max(0, maxInsertLength));
        }

        // Insert the clipboard text (as plain text) at the caret, replacing any current selection.
        // We rely on the browser's native insertText instead of manually reconstructing the value,
        // because reconstruction lost the existing content once the input spanned multiple nodes
        // (e.g. after a line break), where the caret sits inside a nested node.
        document.execCommand('insertText', false, insertText);
        this.bindInput();
    }

    private bindInput(): void {
        if (this.lastViewModel !== this.getText()) {
            // iOS: Autocomplete does not call onKeydown (and not insert prevention happens). For this reason we have to shorten the text after the autocomplete insert.
            const maxLength = this.maxLength;
            if (this.hasMaxInputLength(maxLength) && this.getText().length > maxLength) {
                this.setText(this.getText().substr(0, maxLength));
                this.setCursor(maxLength);
            }
            this.lastViewModel = this.getText();
            this.contenteditableModelChange.emit(this.getText());
        }
    }

    private refreshView(): void {
        this.setText(this.contenteditableModel);
    }

    private hasSelection(): boolean {
        const selection = this.getSelection();
        return selection !== undefined && selection.start !== selection.end;
    }

    private getSelection(): InputSelection | undefined {
        const element = this.elementRef.nativeElement as HTMLElement;
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        if (!window.getSelection) {
            return undefined;
        }
        const selection = window.getSelection();
        // eslint-disable-next-line no-null/no-null
        if (selection === null || selection.rangeCount === 0) {
            return undefined;
        }
        const range = selection.getRangeAt(0);
        // The selection may be inside a nested node (e.g. a <div>/<br> created by a line break),
        // so we measure the caret position against the whole content instead of a direct child.
        if (!element.contains(range.commonAncestorContainer)) {
            return undefined;
        }

        const preCaretRange = range.cloneRange();
        preCaretRange.selectNodeContents(element);
        preCaretRange.setEnd(range.startContainer, range.startOffset);
        const start = preCaretRange.toString().length;
        const end = start + range.toString().length;

        return new InputSelection(start, end);
    }

    private setCursor(position: number): void {
        const element = this.elementRef.nativeElement as HTMLElement;
        const sel = window.getSelection();
        const range = document.createRange();

        const textNode = (element.lastChild ?? element.appendChild(document.createTextNode(''))) as Text;

        range.setStart(textNode, Math.min(position, textNode.length));
        range.collapse(true);

        // eslint-disable-next-line no-null/no-null
        if (sel !== null) {
            sel.removeAllRanges();
            sel.addRange(range);
        }
        element.focus();
    }

    private getText(): string {
        // ElementRef data is any

        return this.elementRef.nativeElement.innerText;
    }

    private setText(text: string): void {
        // ElementRef data is any

        this.elementRef.nativeElement.innerText = text;
    }

    private hasMaxInputLength(maxLength: number | undefined): maxLength is number {
        return maxLength !== undefined && maxLength !== 0;
    }
}
